import { MecProduct } from '@adapters/bigcommerce/types/product';
import { MultiGroupPrices } from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';
import { CompareTableSpec } from '@adapters/bigcommerce/types/compareTable';
import {
  ProductCompareConfig,
  ProductComparisonData,
} from '_components/Product/ProductCompareTable/types';
import { getAttributeType } from '@utils/comparison/getCompareAttribute';

export interface CompareSpec {
  en: string;
  fr: string;
}

const specOrder = ({ displayOrder }: CompareTableSpec): number => {
  const order =
    typeof displayOrder === 'string' && !displayOrder.trim()
      ? NaN
      : Number(displayOrder);

  return Number.isFinite(order) ? order : Infinity;
};

const bySpecOrder = (a: CompareTableSpec, b: CompareTableSpec): number => {
  const orderA = specOrder(a);
  const orderB = specOrder(b);

  return orderA === orderB ? 0 : orderA - orderB;
};

export function buildProductCompareConfig(
  data: ProductComparisonData,
  locale: string,
  productCode?: string
): ProductCompareConfig {
  const key = locale === 'fr' ? 'fr' : 'en';
  const ctaLabel = data.cta?.label?.[key]?.trim();
  const ctaUrl = data.cta?.url?.[key]?.trim();
  const items = data.items ?? [];
  // The seed product leads the table: the first column is the emphasized one.
  // Franchise tables are authored in a deliberate series order and emphasize no
  // column, so the seed stays where merch put it.
  const isFranchise = data.comparisonType === 'franchise';
  const seedIndex =
    productCode && !isFranchise
      ? items.findIndex((item) => item?.id === productCode)
      : -1;
  const orderedItems =
    seedIndex > 0
      ? [
          items[seedIndex],
          ...items.slice(0, seedIndex),
          ...items.slice(seedIndex + 1),
        ]
      : items;

  return {
    heading: {
      line1: data.title?.[key] || '',
    },
    ...(ctaLabel && ctaUrl
      ? { contextLink: { label: ctaLabel, url: ctaUrl } }
      : {}),
    comparisonType: data.comparisonType,
    items: orderedItems,
    attributes: [...(data.comparisonSpecs ?? [])]
      .sort(bySpecOrder)
      .filter((spec) => {
        const types = [spec.code, spec.label?.en, spec.label?.fr].map(
          (specKey) => getAttributeType(specKey ?? '')
        );
        return !types.includes('name') && !types.includes('price');
      })
      .map((spec) => ({ key: spec.code, label: spec.label })),
  };
}

export function buildGroupPricesByCode(
  product: MecProduct,
  customerGroupPrices: MultiGroupPrices | null,
  compareProductsGroupPrices: Record<string, MultiGroupPrices | null> | null
): Record<string, MultiGroupPrices | null> {
  return {
    ...(product.code ? { [product.code]: customerGroupPrices } : {}),
    ...(compareProductsGroupPrices ?? {}),
  };
}
